// v2 vulture — the carrion bird mid-circle: a hunched dark body with a raised
// mantle, the bare pink head on a bare crooked neck rising out of a pale fluffy
// ruff collar, a pale hooked beak with a dark tip, a short dark fan tail and
// broad two-tone wings ending in four ragged finger feathers each. Two liveries:
// brown griffon and a sootier Egyptian-style bird. Ported 1:1 from age-of-mcp's
// v2/vulture.ts, using the library's shared `wildland` (eyes, tube, wingPair)
// helpers.
import * as THREE from "three";
import { defineModel, wildland, type Part } from "../../lib";

const LIVERY = [
  { body: "#38302a", mantle: "#2c2520", wing: "#241e18", ruff: "#e8e0d0",
    skin: "#d09a86", beak: "#d8cfc0" },
  { body: "#2a2622", mantle: "#201c18", wing: "#181410", ruff: "#d8d2c0",
    skin: "#d4c08a", beak: "#c8bca8" },
];

function vultureParts(v: number): Part[] {
  const c = LIVERY[v % LIVERY.length]!;
  const parts: Part[] = [
    // hunched body with a raised mantle over the shoulders
    { geometry: new THREE.IcosahedronGeometry(0.046, 0), color: c.body,
      position: [-0.008, 0.034, 0], rotation: [0, 0, 0.15],
      scale: [1.5, 1.05, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.036, 0), color: c.mantle,
      position: [-0.02, 0.05, 0], scale: [1.3, 0.7, 1.0] },
    // fluffy pale ruff collar ringing the neck base
    { geometry: new THREE.CylinderGeometry(0.02, 0.024, 0.016, 6),
      color: c.ruff, position: [0.04, 0.052, 0], rotation: [0, 0, 0.9] },
    // bare crooked neck + bare head
    wildland.tube([0.045, 0.05, 0], [0.07, 0.066, 0], 0.0085, 0.007, c.skin),
    { geometry: new THREE.IcosahedronGeometry(0.0145, 0), color: c.skin,
      position: [0.077, 0.069, 0], scale: [1.25, 0.95, 0.9] },
    // pale hooked beak with a dark tip
    { geometry: new THREE.ConeGeometry(0.007, 0.022, 4), color: c.beak,
      position: [0.094, 0.066, 0], rotation: [0, 0, -1.15] },
    { geometry: new THREE.IcosahedronGeometry(0.003, 0), color: "#5a4a3c",
      position: [0.1, 0.058, 0] },
    // short dark fan tail
    { geometry: new THREE.ConeGeometry(0.022, 0.055, 4), color: c.wing,
      position: [-0.08, 0.032, 0], rotation: [0, 0, Math.PI / 2],
      scale: [1, 1, 0.4] },
  ];
  parts.push(...wildland.eyes(0.083, 0.073, 0.009, 0.003, "#1c140c"));
  parts.push(...wildland.wingPair({
    root: [0, 0.046, 0.018], span: 0.16, chord: 0.06, rise: 0.022,
    sweep: 0.024, thick: 0.0065,
    inner: c.mantle, outer: c.wing, tip: "#100c08", fingers: 4,
  }));
  return parts;
}

export const vultureV2 = defineModel({
  id: "vulture-v2",
  batchId: "fable",
  name: "Vulture (v2)",
  tags: ["creature","vulture","low-poly","age-of-mcp","fable"],
  summary: "Low-poly vulture.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.13,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => vultureParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => vultureParts(1) },
  ],
  animations: [],
});
